import { supabase } from './supabaseClient';

export type AllowedEmail = {
  id: string;
  email: string;
  added_by: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

const ALLOWLIST_CONFIG = {
  TIMEOUT_PER_ATTEMPT_MS: 5000,
  MAX_RETRIES: 3,
  INITIAL_RETRY_DELAY_MS: 1000,
  MAX_RETRY_DELAY_MS: 8000,
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const maskEmail = (email: string): string => {
  const [local, domain] = email.split('@');
  if (!local || !domain) return '***';
  return `${local.substring(0, 2)}***@${domain}`;
};

export async function isEmailAllowed(email: string): Promise<boolean> {
  const normalizedEmail = email.toLowerCase().trim();
  const startTime = Date.now();

  for (let attempt = 1; attempt <= ALLOWLIST_CONFIG.MAX_RETRIES; attempt++) {
    const attemptStart = Date.now();

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        ALLOWLIST_CONFIG.TIMEOUT_PER_ATTEMPT_MS
      );

      const { data, error } = await supabase.rpc(
        'is_email_allowed',
        { check_email: normalizedEmail },
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);
      const duration = Date.now() - attemptStart;

      if (error) {
        console.warn(
          `[emailAllowlistService] Attempt ${attempt}/${ALLOWLIST_CONFIG.MAX_RETRIES} error (${duration}ms):`,
          error.message
        );

        if (attempt < ALLOWLIST_CONFIG.MAX_RETRIES) {
          const delay = Math.min(
            ALLOWLIST_CONFIG.INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1),
            ALLOWLIST_CONFIG.MAX_RETRY_DELAY_MS
          );
          console.log(`[emailAllowlistService] Retrying in ${delay}ms...`);
          await sleep(delay);
          continue;
        }

        console.warn(
          `[emailAllowlistService] DENYING ${maskEmail(normalizedEmail)} after ${ALLOWLIST_CONFIG.MAX_RETRIES} failed attempts`
        );
        return false;
      }

      const totalDuration = Date.now() - startTime;
      console.log(
        `[emailAllowlistService] Check completed for ${maskEmail(normalizedEmail)}: ${data === true ? 'ALLOWED' : 'DENIED'} (${totalDuration}ms, attempt ${attempt})`
      );
      return data === true;
    } catch (err) {
      const duration = Date.now() - attemptStart;
      const isAbort = err instanceof Error && err.name === 'AbortError';

      console.warn(
        `[emailAllowlistService] Attempt ${attempt}/${ALLOWLIST_CONFIG.MAX_RETRIES} ${isAbort ? 'timeout' : 'exception'} (${duration}ms):`,
        isAbort ? 'Request timed out' : err
      );

      if (attempt < ALLOWLIST_CONFIG.MAX_RETRIES) {
        const delay = Math.min(
          ALLOWLIST_CONFIG.INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1),
          ALLOWLIST_CONFIG.MAX_RETRY_DELAY_MS
        );
        console.log(`[emailAllowlistService] Retrying in ${delay}ms...`);
        await sleep(delay);
        continue;
      }
    }
  }

  const totalDuration = Date.now() - startTime;
  console.warn(
    `[emailAllowlistService] DENYING ${maskEmail(normalizedEmail)} - all ${ALLOWLIST_CONFIG.MAX_RETRIES} attempts failed (${totalDuration}ms total)`
  );
  return false;
}

export async function getAllowedEmails(): Promise<AllowedEmail[]> {
  const { data, error } = await supabase
    .from('allowed_emails')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error(
      '[emailAllowlistService] Error fetching allowed emails:',
      error
    );
    return [];
  }

  return data || [];
}

export async function addAllowedEmail(
  email: string,
  notes?: string
): Promise<AllowedEmail | null> {
  const { data: userData } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('allowed_emails')
    .insert({
      email: email.toLowerCase().trim(),
      added_by: userData?.user?.id || null,
      notes: notes || null,
      is_active: true,
    })
    .select()
    .single();

  if (error) {
    console.error('[emailAllowlistService] Error adding email:', error);
    return null;
  }

  return data;
}

export async function removeAllowedEmail(id: string): Promise<boolean> {
  const { error } = await supabase.from('allowed_emails').delete().eq('id', id);

  if (error) {
    console.error('[emailAllowlistService] Error removing email:', error);
    return false;
  }

  return true;
}

export async function toggleEmailStatus(
  id: string,
  isActive: boolean
): Promise<boolean> {
  const { error } = await supabase
    .from('allowed_emails')
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) {
    console.error(
      '[emailAllowlistService] Error toggling email status:',
      error
    );
    return false;
  }

  return true;
}
